import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Badge, Button, Card, Stat } from "@/components/ui-kit";
import { ExpressRateScheduleEditor } from "@/components/ExpressRateScheduleEditor";
import { formatNaira, useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import {
  fetchAllBorrowers,
  fetchAllLoans,
  fetchPlatformExpressSchedule,
  savePlatformExpressSchedule,
} from "@/lib/data";
import type { ExpressRateBand } from "@/lib/express-rates";
import { isVerifiedUpgradePending, type Borrower, type Loan } from "@/lib/types";

export const Route = createFileRoute("/admin/")({
  component: AdminOverview,
});

const totalDue = (l: Loan) => l.proposedRepay ?? l.amount * (1 + l.interestRate / 100);

function AdminOverview() {
  const { session } = useAuth();
  const isSuper = session?.kind === "admin" && session.role === "super";
  const [borrowers, setBorrowers] = useState<Borrower[]>([]);
  const [loans, setLoans] = useState<Loan[]>([]);
  const [expressSchedule, setExpressSchedule] = useState<ExpressRateBand[]>([]);
  const [scheduleBusy, setScheduleBusy] = useState(false);
  const [scheduleMsg, setScheduleMsg] = useState<string | null>(null);

  const load = async () => {
    const [b, l, schedule] = await Promise.all([
      fetchAllBorrowers(),
      fetchAllLoans(),
      fetchPlatformExpressSchedule(),
    ]);
    setBorrowers(b);
    setLoans(l);
    setExpressSchedule(schedule);
  };

  useEffect(() => {
    load().catch(console.error);
    const channel = supabase
      .channel("admin-overview")
      .on("postgres_changes", { event: "*", schema: "public", table: "loans" }, () => { void load(); })
      .on("postgres_changes", { event: "*", schema: "public", table: "kyc" }, () => { void load(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  const active = loans.filter((l) => l.status === "active" || l.status === "overdue");
  const pendingLoans = loans.filter((l) => l.status === "pending" || l.status === "approved");
  const outstanding = active.reduce((sum, l) => sum + (totalDue(l) - l.paid), 0);
  const totalRepaid = loans.filter((l) => l.status === "repaid").reduce((s, l) => s + l.paid, 0);
  const now = Date.now();
  const dueIn2 = active.filter((l) => {
    const diff = new Date(l.dueDate).getTime() - now;
    return diff > 0 && diff < 2 * 86400000;
  }).length;
  const overdueLoans = active.filter((l) => new Date(l.dueDate).getTime() < now);
  const pendingKyc = borrowers.filter((b) => b.kycStatus === "pending").length;

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Operations</div>
          <h1 className="font-display text-4xl mt-1">Overview</h1>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/borrowers"><Badge tone="gold">Manage borrowers →</Badge></Link>
          <Link to="/admin/loans"><Badge tone="gold">Manage loans →</Badge></Link>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <Stat label="Total members" value={borrowers.length} sub={`${pendingKyc} pending KYC`} />
        <Stat label="Pending loans" value={pendingLoans.length} />
        <Stat label="Active loans" value={active.length} />
        <Stat label="Due in 2 days" value={dueIn2} />
        <Stat label="Overdue" value={overdueLoans.length} />
        <Stat label="Outstanding" value={formatNaira(outstanding)} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl">Pending KYC reviews</h2>
            <Link to="/admin/borrowers" className="text-xs text-gold hover:underline">View all</Link>
          </div>
          <div className="mt-4 space-y-3">
            {borrowers.filter((b) => b.kycStatus === "pending").length === 0 ? (
              <p className="text-sm text-muted-foreground">No pending reviews.</p>
            ) : (
              borrowers
                .filter((b) => b.kycStatus === "pending")
                .map((b) => (
                  <Link
                    key={b.id}
                    to="/admin/borrowers"
                    className="flex items-center justify-between p-3 rounded-md bg-accent/40 hover:bg-accent transition"
                  >
                    <div>
                      <div className="font-medium">{b.name}</div>
                      <div className="text-xs text-muted-foreground">{b.email}</div>
                      {isVerifiedUpgradePending(b) && (
                        <div className="text-xs text-warning mt-1">Applied to upgrade to Verified</div>
                      )}
                    </div>
                    <Badge tone="warning">
                      {isVerifiedUpgradePending(b) ? "Upgrade" : "Review"}
                    </Badge>
                  </Link>
                ))
            )}
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl">Pending loan applications</h2>
            <Link to="/admin/loans" className="text-xs text-gold hover:underline">View all</Link>
          </div>
          <div className="mt-4 space-y-3">
            {pendingLoans.length === 0 ? (
              <p className="text-sm text-muted-foreground">No pending applications.</p>
            ) : (
              pendingLoans.map((l) => {
                const b = borrowers.find((x) => x.id === l.borrowerId);
                return (
                  <Link
                    key={l.id}
                    to="/admin/loans"
                    className="flex items-center justify-between p-3 rounded-md bg-accent/40 hover:bg-accent transition"
                  >
                    <div>
                      <div className="font-medium">{b?.name ?? "Unknown"}</div>
                      {b?.email && (
                        <div className="text-xs text-muted-foreground">{b.email}</div>
                      )}
                      <div className="text-xs text-muted-foreground">
                        {formatNaira(l.amount)} · due {new Date(l.dueDate).toLocaleDateString()}
                      </div>
                    </div>
                    <Badge tone="warning">{l.status === "approved" ? "Disburse" : "Pending"}</Badge>
                  </Link>
                );
              })
            )}
          </div>
        </Card>
      </div>

      {overdueLoans.length > 0 && (
        <Card>
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl">Overdue loans</h2>
            <Link to="/admin/loans" className="text-xs text-gold hover:underline">View all</Link>
          </div>
          <div className="mt-4 space-y-3">
            {overdueLoans.map((l) => {
              const b = borrowers.find((x) => x.id === l.borrowerId);
              return (
                <div key={l.id} className="flex items-center justify-between p-3 rounded-md bg-destructive/5 border border-destructive/20">
                  <div>
                    <div className="font-medium">{b?.name ?? "Unknown"}</div>
                    {b?.email && (
                      <div className="text-xs text-muted-foreground">{b.email}</div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      {formatNaira(totalDue(l) - l.paid)} remaining · due {new Date(l.dueDate).toLocaleDateString()}
                    </div>
                  </div>
                  <Badge tone="danger">Overdue</Badge>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {isSuper && (
        <Card>
          <h2 className="font-display text-xl">Express weekly rates (platform default)</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Applies to all Express borrowers unless a custom schedule is set on their profile. Example: 1–7 days @ 15%, 8–14 @ 30%.
          </p>
          {scheduleMsg && <p className="mt-2 text-sm text-gold">{scheduleMsg}</p>}
          <div className="mt-4">
            <ExpressRateScheduleEditor bands={expressSchedule} onChange={setExpressSchedule} disabled={!isSuper} />
          </div>
          <Button
            className="mt-4"
            variant="gold"
            loading={scheduleBusy}
            onClick={async () => {
              setScheduleBusy(true);
              setScheduleMsg(null);
              try {
                await savePlatformExpressSchedule(expressSchedule);
                setScheduleMsg("Platform Express schedule saved.");
              } catch (e) {
                setScheduleMsg(e instanceof Error ? e.message : "Could not save schedule.");
              } finally {
                setScheduleBusy(false);
              }
            }}
          >
            Save platform schedule
          </Button>
        </Card>
      )}

      <p className="text-xs text-muted-foreground">
        Lifetime repaid: {formatNaira(totalRepaid)}
      </p>
    </div>
  );
}
